/**
 * Rollup persistence (FR-DASH-01, FR-DASH-06).
 *
 * Rollup documents are written by `increment()` **in the same batch as the transaction**
 * (AD-02), so there is no separate write to fail on its own and no trigger to lag behind.
 * The delta arithmetic lives in `core/rollups.ts`; this file only moves it to Firestore.
 */

import {
  collection,
  doc,
  documentId,
  getDocs,
  increment,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type WriteBatch,
} from 'firebase/firestore';
import type { MonthKey } from '../core/dates';
import {
  emptyRollup,
  nestFields,
  recomputeRollup,
  type AccountTypeLookup,
  type Rollup,
} from '../core/rollups';
import { releaseSource, reportSnapshotMetadata, trackWrite } from './syncState';
import { db } from './firebase';
import { toTransactionList } from './transactions';

const COLLECTION = 'rollups';

export function toRollup(monthKey: MonthKey, data: Record<string, unknown>): Rollup {
  const base = emptyRollup(monthKey);
  return {
    ...base,
    ...(data as Partial<Rollup>),
    monthKey,
    byCategory: (data.byCategory as Record<string, number>) ?? {},
    byAccount: (data.byAccount as Rollup['byAccount']) ?? {},
    byBusiness: (data.byBusiness as Rollup['byBusiness']) ?? { business: 0, personal: 0 },
  };
}

/**
 * Watch several months at once.
 *
 * A financial year is twelve documents — one `in` query, twelve reads, and that number
 * does not change as history grows. This is FR-DASH-03 in one function.
 */
export function watchRollups(
  monthKeys: MonthKey[],
  onChange: (rollups: Map<MonthKey, Rollup>) => void,
  onError?: (error: Error) => void,
): () => void {
  if (monthKeys.length === 0) {
    onChange(new Map());
    return () => {};
  }

  // Firestore caps an `in` filter at 30 values; a year is 12, and a wider span is
  // chunked by the caller rather than silently truncated here.
  const keys = monthKeys.slice(0, 30);

  const unsubscribe = onSnapshot(
    query(collection(db, COLLECTION), where(documentId(), 'in', keys)),
    { includeMetadataChanges: true },
    (snapshot) => {
      reportSnapshotMetadata('rollups', snapshot.metadata);
      const result = new Map<MonthKey, Rollup>();
      for (const key of keys) result.set(key, emptyRollup(key));
      for (const document of snapshot.docs) {
        result.set(document.id, toRollup(document.id, document.data()));
      }
      onChange(result);
    },
    (error) => onError?.(error),
  );

  return () => {
    releaseSource('rollups');
    unsubscribe();
  };
}

/**
 * Stage rollup increments onto an existing batch.
 *
 * `merge: true` because the document may not exist yet — the first transaction of a
 * month creates its rollup. `increment()` on a missing field starts from zero, which is
 * exactly right here and exactly wrong for account balances, where a missing field would
 * silently discard the opening balance.
 *
 * The payload is **nested**, not dot-pathed. See `nestFields` — dots in a merge-set key
 * are literal characters, and getting that wrong writes the whole per-category and
 * per-account breakdown into fields nobody reads.
 */
export function stageRollupDeltas(
  batch: WriteBatch,
  monthKey: MonthKey,
  fields: Record<string, number>,
): void {
  if (Object.keys(fields).length === 0) return;

  batch.set(
    doc(db, COLLECTION, monthKey),
    { monthKey, updatedAt: serverTimestamp(), ...nestFields(fields, increment) },
    { merge: true },
  );
}

/**
 * Rebuild a month's rollup from its transactions (FR-DASH-06).
 *
 * The recovery path for drift, and the one the nightly verification will reuse. Reads
 * every transaction in the month — expensive by design, and why it is an explicit action
 * rather than something the dashboard does on load.
 *
 * Returns the recomputed rollup so the caller can report what changed.
 */
export async function recomputeMonth(
  monthKey: MonthKey,
  accountType: AccountTypeLookup,
): Promise<Rollup> {
  const snapshot = await getDocs(
    query(
      collection(db, 'transactions'),
      where('deleted', '==', false),
      where('monthKey', '==', monthKey),
    ),
  );

  const transactions = toTransactionList(snapshot.docs);
  const rollup = recomputeRollup(monthKey, transactions, accountType);

  // Written as a whole document, not as increments: the point is to replace whatever
  // was there, so a partial overwrite would leave the drift in place.
  const batch = writeBatch(db);
  batch.set(doc(db, COLLECTION, monthKey), {
    ...rollup,
    recomputedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await trackWrite(batch.commit());

  return rollup;
}
